// ===================================================================
// src/app/components/common/OwlStatePage.tsx
//
// Layout compartilhado das páginas de estado (404, build desatualizado).
// Visual inspirado em Notion / Linear / Supabase: fundo claro, grid sutil,
// mascote (a coruja da Sophia) num painel escuro, tipografia contida.
// ===================================================================

import type { ReactNode } from 'react';
import owlMascot from '@/assets/coruja-sophia.png';

interface OwlStatePageProps {
  eyebrow: string;
  title: string;
  description: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
}

export function OwlStatePage({
  eyebrow,
  title,
  description,
  actions,
  footer,
}: OwlStatePageProps) {
  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-50 flex items-center justify-center px-6 py-16">
      {/* Grid sutil de fundo */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgb(226 232 240 / 0.7) 1px, transparent 1px), linear-gradient(to bottom, rgb(226 232 240 / 0.7) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent)',
        }}
      />

      <div className="relative w-full max-w-4xl">
        <div className="grid gap-10 md:grid-cols-[auto_1fr] md:gap-12 items-center">
          {/* Mascote */}
          <div className="justify-self-center md:justify-self-start">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-6 rounded-[2.5rem] bg-indigo-500/20 blur-2xl"
              />
              <div className="relative size-40 md:size-48 rounded-[2rem] bg-[#1a1040] ring-1 ring-indigo-900/40 shadow-xl shadow-indigo-950/20 overflow-hidden">
                <img
                  src={owlMascot}
                  alt="Coruja Sophia, mascote da aplicação"
                  className="size-full object-cover scale-110"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-slate-500 shadow-sm">
              {eyebrow}
            </span>

            <h1 className="mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              {title}
            </h1>

            <div className="mt-3 text-sm leading-relaxed text-slate-500 max-w-md mx-auto md:mx-0">
              {description}
            </div>

            {actions && (
              <div className="mt-7 flex flex-wrap items-center justify-center md:justify-start gap-3">
                {actions}
              </div>
            )}

            {footer && (
              <div className="mt-8 border-t border-slate-200 pt-5 text-xs text-slate-400">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OwlStatePage;
